import { useState } from 'react'
import { Modal } from './Modal'
import { CheckboxGroup, type CheckboxOption } from './primitives/CheckboxGroup'
import { Notice } from './primitives/Notice'
import { SUPPORTED_ENTITLEMENTS, type Entitlement } from '../api/types'

// Mint the ATC-ADR-016 `atc_fd_` credential (ATC-131).
//
// ON AN EXISTING ENVIRONMENT. The customerEnvironmentId is supplied, never
// minted: a second GUID would be a second tenant, orphaning the device
// registrations and the account mapping that belong to the first.
export function IssueFlightdeckModal({
  customerEnvironmentId,
  customerName,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  customerEnvironmentId: string
  customerName: string
  busy: boolean
  error: string | null
  onConfirm: (entitledServices: Entitlement[]) => void
  onCancel: () => void
}) {
  // `ppkg` pre-ticked: it is the reason this credential class exists.
  const [checked, setChecked] = useState<string[]>(['ppkg'])

  const options: CheckboxOption[] = SUPPORTED_ENTITLEMENTS.map((value) => ({
    value,
    label: value,
    ...(value === 'ppkg'
      ? { hint: 'The reason this credential class exists — grantable here and nowhere else.' }
      : {}),
  }))

  return (
    <Modal title="Issue a Flight Deck credential" onClose={onCancel}>
      <p>
        Mints an <code>atc_fd_</code> credential on <strong>{customerName || 'this environment'}</strong>{' '}
        (<code className="cei">{customerEnvironmentId}</code>). The environment ID is reused, not
        minted — a second ID would be a second tenant.
      </p>

      <div className="notice notice-warn">
        This credential goes on the Flight Deck <strong>Manager Function App only</strong>. It must
        never be baked into MSI install arguments or reach a device: the ppkg build gate is
        meaningful precisely because devices structurally do not hold it.
      </div>

      <CheckboxGroup
        legend="Entitled services"
        options={options}
        value={checked}
        onChange={setChecked}
        disabled={busy}
      />

      {checked.length === 0 && (
        <Notice tone="warn">A credential entitled to nothing cannot be issued.</Notice>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={busy || checked.length === 0}
          onClick={() => onConfirm(checked as Entitlement[])}
        >
          {busy ? 'Issuing…' : 'Issue credential'}
        </button>
      </div>
    </Modal>
  )
}
